import { RENAULT_BRAND_ID } from '../constants/brands'
import { LABEL_TEXT } from '../constants/messages'
import {
  BINANCE_PARTNER_PROGRAM_URL,
  BINANCE_SIGNUP_URL,
  BINANCE_VIEW_SHOP_URLS,
  COINBASE_BLUR_URL,
  COINBASE_SIGNUP_URL,
  COINBASE_URL,
  CUSTOM_BRAND_ID,
  DONATION_WEBSITE_URLS,
  EXCHANGE_PROVIDER_DATA,
  MAX_CRYPTO_DECIMALS,
  MAX_USD_EXCHANGE_LIMIT,
  MIN_USD_EXCHANGE_LIMIT,
  MIN_USD_EXCHANGE_LIMIT_FOR_MILES
} from '../constants/pointsExchange'
import { X_ACCOUNT_LABEL } from '../constants/social'
import { TOKEN_WHITELIST_TYPE } from '../constants/token'
import { LOYALTY_EVENT_TYPES } from '../constants/transactions'
import { commonParser, parseMembership } from './apiParser.helpers'
import {
  isExchangeProviderEmailType,
  isExistsElementFromList,
  isListNotEmpty
} from './app/app.helpers'
import { getStaticLogoPath } from './brand.helpers'
import { getNftCoverImage } from './nft.helpers'
import { formatCurrencyValue, numberCeil } from './number.helpers'
import {
  formatTicks,
  titleCase,
  toDashCase,
  toLower,
  toUpper
} from './string.helpers'

const formatMembershipsToObject = (membershipData) => {
  if (membershipData && membershipData.length) {
    return membershipData.reduce((obj, data) => {
      const membership = parseMembership(data)
      const brand = membership.brand
      return brand && brand.id
        ? {
            ...obj,
            [brand.id]: {
              id: membership.id,
              membershipNumber: membership.membershipNumber
            }
          }
        : {}
    }, {})
  }
  return {}
}

const isProviderConnected = (memberships, provider) => {
  return Boolean(extractMembershipNumber(memberships, provider))
}

const extractMembershipNumber = (memberships, provider) => {
  if (
    memberships &&
    Object.keys(memberships).length > 0 &&
    provider &&
    provider.brandId
  ) {
    return memberships?.[provider?.brandId]?.membershipNumber
  }
  return ''
}

const exchangeProviderTitleForTransaction = (t, { name, id }, symbol) => {
  if (isCryptoBrand(id)) {
    return t('exchange.cryptocurrencies') + ` (${toUpper(symbol)})`
  } else if (isBrandIdPaypal(id)) {
    return t('exchange.fiat-currencies') + ` (${toUpper(symbol)})`
  } else if (isBrandIdGetChange(id)) {
    return t('exchange.donations') + ` (${toUpper(symbol)})`
  } else if (isTransactionForMiles(id)) {
    const type = EXCHANGE_PROVIDER_DATA[id]?.type
    return `${name}${type ? ` ${type}` : ''} ${titleCase(
      t(buildMilesOrPointsLabel({ isMiles: true }))
    )}`
  } else {
    return name
  }
}

const isTransactionForMiles = (brandId) => {
  return [CUSTOM_BRAND_ID.ETIHAD, CUSTOM_BRAND_ID.MILES_AND_MORE].includes(
    brandId
  )
}

const isBrandIdCoinbase = (brandId) => {
  return brandId === CUSTOM_BRAND_ID.COINBASE
}

const isBrandIdBinance = (brandId) => {
  return brandId === CUSTOM_BRAND_ID.BINANCE
}

const isCryptoBrand = (brandId) => {
  return isExistsElementFromList(
    [CUSTOM_BRAND_ID.COINBASE, CUSTOM_BRAND_ID.BINANCE],
    brandId
  )
}

const isBrandIdPaypal = (brandId) => {
  return brandId === CUSTOM_BRAND_ID.PAYPAL
}

const isBrandIdXoxo = (brandId) => {
  return brandId === CUSTOM_BRAND_ID.XOXO_DAY
}

const isBrandIdGetChange = (brandId) => {
  return brandId === CUSTOM_BRAND_ID.GET_CHANGE
}

const exchangeLabelForAccount = (t, provider) => {
  if (!provider) {
    return ''
  }
  const { name, symbol, brandId, isMiles, isCrypto, isFiat, type, brandName } =
    provider
  const isDonation = isTokenTypeDonation(type)
  let label
  if (isCrypto) {
    label = t('exchange.cryptocurrencies')
  } else if (isFiat) {
    label = t('exchange.fiat-currencies')
  } else if (isDonation) {
    label = t('exchange.donations')
  }
  return label
    ? label + ` (${brandName})`
    : exchangeProviderLabel({ name, symbol, brandId, isMiles })
}

const exchangeProviderLabel = ({ name, symbol }) => {
  return `${name} ${symbol ? `(${symbol})` : ''}`
}

const isTokenTypeMiles = (type) => {
  return type === TOKEN_WHITELIST_TYPE.MILES
}

const isTokenTypeNft = (type) => {
  return type === TOKEN_WHITELIST_TYPE.ERC_721
}

const isTokenTypeCrypto = (type) => {
  return type === TOKEN_WHITELIST_TYPE.CRYPTO
}

const isTokenTypeFiat = (type) => {
  return type === TOKEN_WHITELIST_TYPE.FIAT
}

const isTokenTypeGiftCard = (type) => {
  return type === TOKEN_WHITELIST_TYPE.GIFT_CARD
}

const isTokenTypeDonation = (type) => {
  return type === TOKEN_WHITELIST_TYPE.DONATION
}

const isTokenTypePoints = (type) => {
  return [TOKEN_WHITELIST_TYPE.ERC_20, TOKEN_WHITELIST_TYPE.EXTERNAL].includes(
    type
  )
}

const isTokenTypesForExchange = (type) => {
  return [
    TOKEN_WHITELIST_TYPE.ERC_20,
    TOKEN_WHITELIST_TYPE.EXTERNAL,
    TOKEN_WHITELIST_TYPE.MILES,
    TOKEN_WHITELIST_TYPE.CRYPTO,
    TOKEN_WHITELIST_TYPE.FIAT,
    TOKEN_WHITELIST_TYPE.DONATION,
    TOKEN_WHITELIST_TYPE.GIFT_CARD,
    TOKEN_WHITELIST_TYPE.MERCHANDISE
  ].includes(type)
}

const buildMilesOrPointsLabel = (provider) => {
  if (!provider) {
    return ''
  }
  const { isMiles, isCrypto } = provider
  return isMiles
    ? LABEL_TEXT.MILES
    : isCrypto
      ? LABEL_TEXT.CRYPTO
      : LABEL_TEXT.POINTS
}

const exchangeProviderSymbol = (t, provider) => {
  if (!provider) {
    return ''
  }
  const { symbol, isMiles } = provider
  return isMiles ? t(LABEL_TEXT.MILES) : symbol
}

const exchangeProviderAccountLabel = (t, { brandId }, isLower = false) => {
  const label = buildAccountLabel(brandId, t)
  return isLower ? toLower(label) : label
}

const buildAccountLabel = (brandId, t) => {
  if (brandId === CUSTOM_BRAND_ID.TELEGRAM) {
    return t('common.social-username', { name: 'Telegram' })
  } else if (brandId === CUSTOM_BRAND_ID.X) {
    return t('common.social-username', { name: X_ACCOUNT_LABEL })
  } else if (brandId === CUSTOM_BRAND_ID.QBX_TAP) {
    return 'QBX Tap TG User ID'
  } else if (isExchangeProviderEmailType(brandId)) {
    return t('common.email-address')
  }
  return t('exchange.account-number')
}

const isExchangeAvailable = (exchangeWhitelist) => {
  return isListNotEmpty(exchangeWhitelist.outgoing)
}

const filterExchangeProviderForExchange = (exchangeWhitelist) => {
  const partnersObj = exchangeWhitelist.reduce((acc, partner) => {
    if (
      !hasIgnoredBrandIdsForExchange(partner.brandId) &&
      isTokenTypesForExchange(partner.type)
    ) {
      acc[partner.tokenId] = partner
      // @TODO: Need to remove if not using
      // const groupKey = toLower(partner.symbol)
      // if (isTokenTypeCrypto(partner.type)) {
      //   if (!acc[groupKey]) {
      //     acc[groupKey] = partner
      //     acc[groupKey].additionalList = []
      //   } else {
      //     acc[groupKey].additionalList.push(partner)
      //   }
      // } else {
      //   acc[partner.tokenId] = partner
      // }
    }
    return acc
  }, {})
  return sortDataByNameASC(Object.values(partnersObj))
}

const filterExchangeProviderForManage = (exchangeWhitelist) => {
  const providers = {}
  exchangeWhitelist.outgoing.forEach((exchange) => {
    if (
      !providers[exchange.brandId] &&
      isTokenTypesForExchange(exchange.type)
    ) {
      exchange.isOauthBrand = isOauthBrand(exchange.brandId)
      providers[exchange.brandId] = exchange
    }
  })
  return sortDataByNameASC(Object.values(providers))
}

const hasIgnoredBrandIdsForExchange = (brandId) => {
  return isExistsElementFromList(
    [
      CUSTOM_BRAND_ID.TROLLBEADS_MEMBERSHIP_CLUB,
      CUSTOM_BRAND_ID.TELEGRAM,
      CUSTOM_BRAND_ID.X,
      CUSTOM_BRAND_ID.QBX_TAP,
      CUSTOM_BRAND_ID.DISCORD
    ],
    brandId
  )
}

const isOauthBrand = (brandId) => {
  return isExistsElementFromList(
    [CUSTOM_BRAND_ID.X, CUSTOM_BRAND_ID.DISCORD],
    brandId
  )
}

const buildSignUpUrl = async (provider) => {
  const { isCrypto, isFiat, loyaltyProgramUrl, websiteUrl, brandId, metadata } =
    provider
  if (isCrypto) {
    return isBrandIdCoinbase(brandId) ? COINBASE_SIGNUP_URL : BINANCE_SIGNUP_URL
  } else if (isFiat) {
    return loyaltyProgramUrl
  } else {
    if (loyaltyProgramUrl) {
      return `${loyaltyProgramUrl}/${buildSignupPath(brandId)}`
    }
    return metadata?.links?.websiteUrl || websiteUrl || ''
  }
}

const buildVisitProgramUrl = (provider, defaultUrl) => {
  const { isCrypto, brandId } = provider
  return isCrypto && isBrandIdBinance(brandId)
    ? BINANCE_PARTNER_PROGRAM_URL
    : defaultUrl
}

const buildSignupPath = (brandId) => {
  return brandId === RENAULT_BRAND_ID ? 'registrazione' : 'signup'
}

const formatCryptoValue = (points, offramp = 0, cryptoExchange = 0) => {
  return cryptoExchange > 0
    ? Math.round(
        formatCurrencyValue((points * offramp) / cryptoExchange, 6) *
          MAX_CRYPTO_DECIMALS
      ) / MAX_CRYPTO_DECIMALS || 0
    : 0
}

const ceilCalculator = (rate, value) => {
  return formatTicks(Math.ceil(rate * value))
}

const floorCalculator = (value) => {
  return Math.floor(value) || 0
}

const sortDataByNameASC = (partners) => {
  return partners?.sort((a, b) => a?.name?.localeCompare(b?.name))
}

const sortExchangesByNameASC = (exchanges) => {
  return exchanges?.sort((a, b) => a?.brandName?.localeCompare(b?.brandName))
}

const buildCoinbaseShopUrl = (tokenName) => {
  const name = toDashCase(tokenName)
  return name === 'blur'
    ? COINBASE_BLUR_URL
    : `${COINBASE_URL}learn/crypto-basics/what-is-${name}`
}

const buildBinanceShopUrl = (tokenSymbol) => {
  return BINANCE_VIEW_SHOP_URLS[toLower(tokenSymbol)]
}

const isExchangeEventType = (loyaltyEventType) => {
  return [
    LOYALTY_EVENT_TYPES.EXCHANGE,
    LOYALTY_EVENT_TYPES.REVERT_EXCHANGE
  ].includes(loyaltyEventType)
}

const isNftSaleEventType = (loyaltyEventType) => {
  return loyaltyEventType === LOYALTY_EVENT_TYPES.NFT_SALE
}

const formattedNftsForRedemption = ({ nftsForRedemption, tokenProfile }) => {
  const tokenOnramp = tokenProfile?.onramp
  return nftsForRedemption
    .filter(({ canRedeem }) => canRedeem)
    .map(({ id, onramp, name, metadata }) => {
      return {
        id,
        isNft: true,
        claimCount: 0,
        value: tokenOnramp > 0 ? numberCeil(onramp / tokenOnramp) : 0,
        name,
        metadata: {
          ...metadata,
          description: metadata?.description?.data,
          coverImage: getNftCoverImage(metadata)
        },
        website: metadata?.externalLink
      }
    })
}

const formattedNftsForSale = ({ nftsForSale, tokenProfile }) => {
  const tokenOnramp = tokenProfile?.onramp
  return nftsForSale?.map(
    ({ nftId, usdPrice, claimCount, token: { id, name, metadata } }) => {
      return {
        id,
        nftId,
        isNft: true,
        isForSale: true,
        claimCount,
        value: tokenOnramp > 0 ? numberCeil(usdPrice / tokenOnramp) : 0,
        usdPrice,
        name,
        metadata: {
          ...metadata,
          description: metadata?.description?.data,
          coverImage: getNftCoverImage(metadata)
        },
        website: metadata?.externalLink
      }
    }
  )
}

const sortRedeemOptionsByValue = (rewardTypes, nfts) => {
  return [...rewardTypes, ...nfts]?.sort((a, b) => b.value - a.value)
}

const filterSameRedeemOptions = (formattedNfts, formattedSaleNfts) => {
  const redeemOptions = [...formattedNfts, ...formattedSaleNfts]
  const redeemOptionsArray = []
  const seenIds = {}
  redeemOptions.forEach((redeemOption) => {
    if (seenIds[redeemOption.id]) {
      const existingItemIndex = seenIds[redeemOption.id] - 1
      const existingItem = redeemOptionsArray[existingItemIndex]
      if (
        existingItem.metadata.exclusiveContent &&
        redeemOption.metadata.exclusiveContent
      ) {
        if (
          (existingItem.claimCount === 0 && redeemOption.claimCount === 0) ||
          (existingItem.claimCount > 0 && redeemOption.claimCount > 0)
        ) {
          if (redeemOption.value < existingItem.value) {
            redeemOptionsArray[existingItemIndex] = redeemOption
          }
        } else {
          redeemOptionsArray.push(redeemOption)
        }
      } else {
        if (redeemOption.value < existingItem.value) {
          redeemOptionsArray[existingItemIndex] = redeemOption
        }
      }
    } else {
      seenIds[redeemOption.id] = redeemOptionsArray.length + 1
      redeemOptionsArray.push(redeemOption)
    }
  })
  return redeemOptionsArray
}

const isTokenTypeHasStaticUrl = (type) => {
  return isExistsElementFromList(
    [
      TOKEN_WHITELIST_TYPE.CRYPTO,
      TOKEN_WHITELIST_TYPE.FIAT,
      TOKEN_WHITELIST_TYPE.DONATION,
      TOKEN_WHITELIST_TYPE.MERCHANDISE
    ],
    type
  )
}

const buildDonationWebsiteUrl = ({ symbol, websiteUrl }) => {
  return symbol ? DONATION_WEBSITE_URLS[toLower(symbol)] : websiteUrl
}

const selectExchangeRate = (cryptoExchanges, provider) => {
  return cryptoExchanges?.[toLower(provider?.symbol)]?.[provider?.brandId] || 0
}

const calculateMinMaxPoints = (
  { minUsdExchangeLimit, maxUsdExchangeLimit },
  value
) => {
  const minPoints = value > 0 ? Math.ceil(minUsdExchangeLimit / value) : 0
  const maxPoints = value > 0 ? Math.floor(maxUsdExchangeLimit / value) : 0
  return {
    minPoints,
    maxPoints
  }
}

const formatExchangeWhitelist = (data) => {
  const result = {
    outgoing: [],
    incoming: []
  }
  // Using outgoing paris for exchange to other tokens (crypto, points, miles, etc..)
  if (data.outgoing && data.outgoing.length) {
    result.outgoing = data.outgoing
      .filter(({ token }) => token.brand_id !== 'branded_redemption_shop')
      .map((pair) => {
        const {
          token,
          maxUsdExchangeLimit,
          minUsdExchangeLimit,
          fromTokenOfframpOverride,
          toTokenOnrampOverride
        } = commonParser(pair)
        const { id, type, onramp, metadata, ...restAttrs } = token
        const currentToken = commonParser(data.token)
        const exchangeOfframp = fromTokenOfframpOverride || currentToken.offramp
        const exchangeOnramp = toTokenOnrampOverride || onramp
        return {
          description: metadata?.description?.data,
          tokenId: id,
          type,
          exchangeRate: exchangeOfframp / exchangeOnramp,
          exchangeOfframp,
          isMiles: isTokenTypeMiles(type),
          isCrypto: isTokenTypeCrypto(type),
          isFiat: isTokenTypeFiat(type),
          metadata,
          maxUsdExchangeLimit,
          minUsdExchangeLimit,
          ...restAttrs
        }
      })
  }
  // Using incoming paris for buy points with miles (Miles and more for now)
  if (data.incoming && data.incoming.length) {
    result.incoming = data.incoming.map((pair) => {
      const { token, maxUsdExchangeLimit, minUsdExchangeLimit } =
        commonParser(pair)
      const { metadata, logoUrl, ...restAttrs } = token
      return {
        description: metadata?.description?.data,
        metadata,
        maxUsdExchangeLimit: maxUsdExchangeLimit || MAX_USD_EXCHANGE_LIMIT,
        minUsdExchangeLimit:
          minUsdExchangeLimit ||
          (isTokenTypeMiles(token.type)
            ? MIN_USD_EXCHANGE_LIMIT_FOR_MILES
            : MIN_USD_EXCHANGE_LIMIT),
        bgImage: logoUrl,
        ...restAttrs
      }
    })
  }
  return result
}

const buildDropdownListData = (data) => {
  return data.map(
    ({
      tokenId,
      name,
      symbol,
      logoUrl,
      type,
      isCrypto,
      isMiles,
      brandName
    }) => ({
      value: tokenId,
      label: buildDropdownLabel({ name, symbol, isCrypto, brandName }),
      symbol,
      logoUrl,
      type,
      isCrypto,
      isMiles
    })
  )
}

const buildDropdownLabel = (provider) => {
  if (provider) {
    const { name, symbol, isCrypto, brandName } = provider
    return `${name} (${symbol})${isCrypto ? ` - ${brandName}` : ''}`
  } else {
    return ''
  }
}

const buildProviderImageProps = ({
  provider: { symbol, logoUrl, type },
  isForceBrandIcon
}) => {
  const providerIcon =
    isTokenTypeHasStaticUrl(type) && !isForceBrandIcon
      ? getStaticLogoPath(symbol)
      : logoUrl
  return providerIcon ? { src: providerIcon } : { icon: toUpper(symbol[0]) }
}

const checkExchangeGating = (provider, metadata) => {
  if (provider?.isCrypto) {
    return metadata.pointsToCrypto
  } else if (provider?.isMiles) {
    return metadata.pointsToMiles
  }
  return metadata.pointsToPoints
}

const xScreenName = (screenName) => {
  return screenName ? `@${screenName}` : ''
}

export {
  buildBinanceShopUrl,
  buildCoinbaseShopUrl,
  buildDonationWebsiteUrl,
  buildDropdownLabel,
  buildDropdownListData,
  buildMilesOrPointsLabel,
  buildProviderImageProps,
  buildSignUpUrl,
  buildVisitProgramUrl,
  calculateMinMaxPoints,
  ceilCalculator,
  exchangeLabelForAccount,
  exchangeProviderAccountLabel,
  exchangeProviderLabel,
  exchangeProviderSymbol,
  exchangeProviderTitleForTransaction,
  extractMembershipNumber,
  filterExchangeProviderForExchange,
  filterExchangeProviderForManage,
  filterSameRedeemOptions,
  floorCalculator,
  formatCryptoValue,
  formatExchangeWhitelist,
  formatMembershipsToObject,
  formattedNftsForRedemption,
  formattedNftsForSale,
  hasIgnoredBrandIdsForExchange,
  isBrandIdBinance,
  isBrandIdCoinbase,
  isBrandIdGetChange,
  isBrandIdPaypal,
  isBrandIdXoxo,
  isCryptoBrand,
  isExchangeAvailable,
  isExchangeEventType,
  isNftSaleEventType,
  isProviderConnected,
  isTokenTypeCrypto,
  isTokenTypeDonation,
  isTokenTypeFiat,
  isTokenTypeGiftCard,
  isTokenTypeHasStaticUrl,
  isTokenTypeMiles,
  isTokenTypeNft,
  isTokenTypePoints,
  selectExchangeRate,
  sortDataByNameASC,
  sortExchangesByNameASC,
  sortRedeemOptionsByValue,
  checkExchangeGating,
  xScreenName
}
